import * as React from "react";
import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";

import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTablePagination,
  DataTablePaginationProps,
} from "@/components/data-table/data-table-pagination";
import { Loader2 } from "lucide-react";
import {
  TooltipContent,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DataTableProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * @type TanstackTable<TData>
   */
  table: TanstackTable<TData>;

  /**
   * The floating bar to render at the bottom of the table on row selection.
   * @default null
   * @type React.ReactNode | null
   * @example floatingBar={<TasksTableFloatingBar table={table} />}
   */
  floatingBar?: React.ReactNode | null;
  getCanPreviousPage?: DataTablePaginationProps<TData>["getCanPreviousPage"];
  getCanNextPage?: DataTablePaginationProps<TData>["getCanNextPage"];
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (pageIndex: number) => void;

  disablePagination?: boolean;

  onRowClick?: (row: TData, options?: { getNavigationUrl?: boolean }) => void | string;
  isPending?: boolean;

  tableContainerClassName?: string;
  
  /**
   * Function to get custom class names for a table row
   * @param row The row data
   * @returns A string of class names
   */
  getRowClassName?: (row: TData) => string;
}

export function DataTable<TData>({
  table,
  floatingBar = null,
  children,
  className,
  tableContainerClassName,
  getCanPreviousPage,
  getCanNextPage,
  onPageSizeChange,
  onPageChange,
  onRowClick,
  isPending,
  disablePagination,
  getRowClassName,
  ...props
}: DataTableProps<TData>) {
  return (
    <div
      className={cn("w-full space-y-2.5 overflow-auto", className)}
      {...props}
    >
      {children}
      <div
        className={cn(
          "overflow-hidden rounded-[20px] border",
          tableContainerClassName,
        )}
      >
        <Table>
          <TableHeader className="bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="text-zinc-400">
                      {header.isPlaceholder ? null : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </div>
                            </TooltipTrigger>
                            {(header.column.columnDef as any)?.tooltip && (
                              <TooltipContent className="max-w-sm text-center">
                                <span>
                                  {(header.column.columnDef as any)?.tooltip}
                                </span>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isPending ? (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  <div className="flex h-full w-full items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn("border-0", {
                    "cursor-pointer hover:bg-neutral-100": onRowClick,
                  }, getRowClassName?.(row.original))}
                  onClick={(event) => {
                    if (onRowClick) {
                      if (event.metaKey || event.ctrlKey) {
                        const url = onRowClick(row.original, { getNavigationUrl: true });
                        if (url && typeof url === 'string') {
                          event.preventDefault();
                          window.open(url, '_blank');
                          return;
                        }
                      }
                      onRowClick(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell, idx) => (
                    <TableCell key={cell.id} className="relative">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                      {
                        // Add a vertical line to the right of the cell
                        idx === row.getVisibleCells().length - 1 ? null : (
                          <div className="absolute bottom-4 right-0 top-4 h-[auto] w-px rounded bg-neutral-200" />
                        )
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!disablePagination && (
        <div className="flex flex-col gap-2.5">
          <DataTablePagination
            table={table}
            getCanPreviousPage={getCanPreviousPage}
            getCanNextPage={getCanNextPage}
            onPageSizeChange={onPageSizeChange}
            onPageChange={onPageChange}
          />
          {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
        </div>
      )}
    </div>
  );
}
